import path from "path";

import chalk from "chalk";
import { emptyDir, pathExists } from "fs-extra";
import notifier from "node-notifier";

import type { BuildTask } from "../../../../Generator/src/generators/generate";
import { engineFactory } from "../../BuildTasks/Engine/AcquireFormEngine";
import { getEngineSourceKey } from "../../BuildTasks/Engine/EngineSource";
import { compileForm } from "../../BuildTasks/Form/CompileForm";
import { generateForm } from "../../BuildTasks/Form/GenerateForm";
import { NullFormGenerator } from "../../BuildTasks/Form/NullFormGenerator";
import { writeFLangNormalizationFiles } from "../../BuildTasks/Form/WriteFLangNormalizationFiles";
import { FSTask } from "../../BuildTasks/FSTask";
import {
    generatorFactory,
    GeneratorSource,
    getGeneratorSourceKey,
} from "../../BuildTasks/Generator/AcquireFormGenerator";
import { resolveEngineVersion, resolveGeneratorVersion } from "../../BuildTasks/PublishedVersionUtils";
import { FormBuildMode, resolveFormInfo } from "../../BuildTasks/ResolveFormInfo";
import { writeFormMeta } from "../../BuildTasks/WriteFormMeta";
import { ConsoleApplicationContext } from "../../Commons/ConsoleApplication/ConsoleApplicationContext";
import { FormGlobs } from "../../Commons/FormGlobsUtils";
import { ILogger } from "../../Commons/Logging/ILogger";
import { getLogger } from "../../Commons/Logging/StaticLogger";
import { Observe } from "../../Commons/TasksCore/Observe";
import { SingleValueProducer } from "../../Commons/TasksCore/SingleValueProducer";
import { printTime } from "../../Commons/TimingUtils";
import { BuildCache } from "../../FormWebpackConfiguration/CacheAssetsPlugin";
import { reject } from "../../../../Common/TypingUtils";
import { writeAttachmentPathFiles } from "../../BuildTasks/Form/WriteAttachmentPathFiles";
import { ChainableValueProducer } from "../../Commons/TasksCore/ChainableValueProducer";
import { EmptyProducer } from "../../Commons/TasksCore/EmptyProducer";
import { getRngSchemaFileExtension } from "../../../../Common/GetRngSchemaFileExtension";
import { extractGfvFromFormPath } from "../../../../Common/ExtractGFVPath/ExtractGFVPath";
import { WellKnownDirectories } from "../../../../Common/WellKnownDirectories";
import { writeAttachmentPathByGfvFiles } from "../../BuildTasks/Form/WriteAttachmentPathByGfvFiles";
import { writeKClangFiles } from "../../BuildTasks/Form/WriteKClangFiles";
import { writeExtendedSchemaFiles } from "../../BuildTasks/Form/WriteExtendedSchemaFiles";
import { createClientViteConfigTask } from "../../BuildTasks/EngineStructureUtils";
import { BuildCandyWidgetTask } from "../../BuildTasks/CandyWidget/BuildCandyWidgetTask";

import { BuildFormsOptions } from "./BuildFormsOptions";

function getGeneratorVersion(generatorSource: GeneratorSource): string {
    if (generatorSource.type === "PublishedGenerator") {
        return generatorSource.version;
    } else {
        throw new Error("InvalidProgramState");
    }
}

export async function runBuildForms(
    context: ConsoleApplicationContext,
    options: BuildFormsOptions,
    farmDirectory: string,
    resourcesDirectory: string,
    engineDirectory: string,
    candyModulesDirectory: string
): Promise<void> {
    const buildOptions = options;

    const pickListsChain = Observe.files(path.join(farmDirectory, "picklists"), "*.json").do(x =>
        FSTask.copyRecursively(context.logger.withPrefix("PICKLISTS"), x, path.join(resourcesDirectory, "picklists"))
    );

    const formInfos = await FormGlobs.resolveGfvGlobs(options.formGlobs, farmDirectory);

    const staticResourcesForProductionChain = Observe.files(
        path.join(__dirname, "..", "..", "StaticFilesForProductionResourcesDir"),
        "*.js",
        "*.js.map"
    ).do(x =>
        FSTask.copyRecursively(getLogger().withPrefix("STATICS"), x, path.join(resourcesDirectory, "public", "scripts"))
    );

    /** Конвертеры описи для форм, которые пока не реализованы на кенди, сама механика описи перенесена в формы в кенди апи, поэтому они должны быть в candy.resources
     *  Реализуется только при общем билде форм: candy build *
     * */
    let resourcesExternalConvertersChain: ChainableValueProducer<string> | EmptyProducer = Observe.empty();
    if (options.formGlobs[0] === "*") {
        resourcesExternalConvertersChain = Observe.files(path.join(farmDirectory, "externalConverters"), "*.xslt").do(
            x =>
                FSTask.copyRecursively(
                    getLogger().withPrefix("EXTERNAL-CONVERTERS"),
                    x,
                    path.join(resourcesDirectory, "converters")
                )
        );
    }

    const copiedEngines = new Set<string>();

    const repositoryRoot = path.join(farmDirectory, "..");
    const buildCache = new BuildCache(farmDirectory, options.buildCacheMode);

    const createChainForForm = async (formRelativePath: string) => {
        const gfv = extractGfvFromFormPath(formRelativePath);
        const formDirPath = path.join(repositoryRoot, formRelativePath);

        if (!(await pathExists(path.join(formDirPath, WellKnownDirectories.FormJsonFileName)))) {
            context.logger.warning(
                `Form ${gfv} does not have form.json file. To include this form to build process restart build`
            );
            return Observe.value({});
        }
        const formSchemasDirPath = path.join(formDirPath, "schemas");
        const rngSchemaExtension = getRngSchemaFileExtension(formSchemasDirPath, gfv);
        const rngSchemaMask = `*${rngSchemaExtension}`;

        if (rngSchemaExtension == ".rng.xml") {
            throw new Error(
                `Недопустимое название схемы ${gfv + rngSchemaExtension} используй команду candy rename ${gfv} в каталоге candy.farm`
            );
        }
        if (await pathExists(path.join(formDirPath, "presentations.json"))) {
            throw new Error(
                `presentations.json больше не используется, удали его из ${formDirPath} или используй команду candy rename ${gfv} в каталоге candy.farm`
            );
        }

        return Observe.file(path.join(formDirPath, WellKnownDirectories.FormJsonFileName))
            .transform(formJsonFile =>
                resolveFormInfo(
                    formJsonFile,
                    formDirPath,
                    context.logger.withPrefix(`FORM:${gfv}`),
                    options.upgradeFormJson,
                    options.useLastStableVersion,
                    options.usePrereleaseVersion
                )
            )
            .transform(async fi => {
                if (fi.mode === FormBuildMode.NormalBuild) {
                    if (fi.generatorVersion == undefined) {
                        throw new Error("generatorVersion is not defined");
                    }
                    if (fi.engineVersion == undefined) {
                        throw new Error("engineVersion is not defined");
                    }
                    return {
                        ...fi,
                        generatorSource: await resolveGeneratorVersion(
                            fi.generatorVersion,
                            context.logger.withPrefix(gfv)
                        ),
                        engineSource: await resolveEngineVersion(fi.engineVersion, context.logger.withPrefix(gfv)),
                    };
                } else {
                    return {
                        ...fi,
                        generatorSource: undefined,
                        engineSource: undefined,
                    };
                }
            })
            .attach(
                async fi => {
                    if (fi.generatorSource == undefined) {
                        return new SingleValueProducer(new NullFormGenerator());
                    } else {
                        return generatorFactory(fi.generatorSource, candyModulesDirectory, options.useVersionFromCache);
                    }
                },
                async (fi, y) => ({ ...fi, generator: y }),
                async fi =>
                    fi.generatorSource != undefined ? getGeneratorSourceKey(fi.generatorSource) : "NullFormGenerator"
            )
            .attach(
                async fi => {
                    if (fi.engineSource === undefined) {
                        return new SingleValueProducer("NullEngine");
                    }
                    return engineFactory(fi.engineSource, candyModulesDirectory, options.useVersionFromCache).do(
                        async x => {
                            if (!copiedEngines.has(getEngineSourceKey(fi.engineSource))) {
                                await FSTask.copyRecursively(
                                    getLogger().withPrefix("ENGINE"),
                                    x,
                                    path.join(resourcesDirectory, "public")
                                );
                                copiedEngines.add(getEngineSourceKey(fi.engineSource));
                            }
                        }
                    );
                },
                async fi => fi,
                async fi => (fi.engineSource != undefined ? getEngineSourceKey(fi.engineSource) : "NullEngine")
            )
            .attach(async fi =>
                buildOptions.watch
                    ? Observe.files(
                          fi.formSourcesPath,
                          "../../controls/**/*",
                          "../../xsltTemplates/**/*",
                          "../../jsHelpers/**/*",
                          `./${WellKnownDirectories.FormJsonFileName}`,
                          "schemas/**/*",
                          "sugar/**/*",
                          "controls/**/*",
                          "autocalc/**/*",
                          "helpers/**/*",
                          "normalizers/**/*",
                          "validations/**/*",
                          "localization/**/*"
                      )
                    : Observe.value(fi.formSourcesPath)
            )
            .transform(async fi => {
                if (fi.mode !== FormBuildMode.NormalBuild) {
                    return {
                        ...fi,
                        dependenciesHash: undefined,
                    };
                }
                const dependenciesHash = await fi.generator.getAllDependenciesHash(formDirPath, farmDirectory);
                return {
                    ...fi,
                    dependenciesHash: dependenciesHash,
                };
            })
            .transform(async fi => {
                let buildCacheExist: boolean = false;
                if (fi.dependenciesHash != undefined && fi.generatorSource != undefined) {
                    buildCacheExist = await buildCache.exists(fi.generatorSource, gfv, fi.dependenciesHash);
                }
                return {
                    ...fi,
                    buildCacheExist: buildCacheExist,
                };
            })
            .do(async fi => {
                if (fi.generatorSource == undefined) {
                    return undefined;
                }
                if (fi.buildCacheExist) {
                    context.logger.withPrefix(`GENERATE:${gfv}`).log("Skipped due build cache exits");
                    return undefined;
                }

                const task: BuildTask = {
                    gfv: fi.formName,
                    formPath: fi.formSourcesPath,
                    farmDirectory: farmDirectory,
                    resourcesHash: fi.dependenciesHash ?? "UndefinedResourcesHash",
                };
                return generateForm(task, fi.generator, undefined);
            })
            .attach(async () =>
                buildOptions.watch
                    ? Observe.files(path.join(formDirPath, "form"), "**/*")
                    : Observe.value(path.join(formDirPath, "form"))
            )
            .do(async fi => {
                if (fi.generatorSource == undefined) {
                    return;
                }
                if (fi.dependenciesHash != undefined && fi.buildCacheExist) {
                    context.logger.withPrefix(`COMPILE:${gfv}`).log("Skipped due build cache exists");
                    await buildCache.extract(fi.generatorSource, gfv, fi.dependenciesHash, resourcesDirectory);
                    return;
                }
                const buildCacheOptions =
                    fi.dependenciesHash != undefined
                        ? {
                              depsHash: fi.dependenciesHash,
                              generatorVersion: getGeneratorVersion(fi.generatorSource),
                              gfv: gfv,
                              mode: options.buildCacheMode,
                              farmDirectory: farmDirectory,
                          }
                        : undefined;

                await compileForm(
                    fi.formName,
                    formDirPath,
                    resourcesDirectory,
                    fi.dependenciesHash ??
                        reject(
                            "This version of generator is not supported due to dependencies extraction is not supported"
                        ),
                    false,
                    undefined,
                    options.mode === "production",
                    buildCacheOptions
                );

                await writeFormMeta(
                    resourcesDirectory,
                    gfv,
                    fi.engineSource,
                    fi.dependenciesHash ??
                        reject(
                            "This version of generator is not supported due to dependencies extraction is not supported"
                        ),
                    fi.themes,
                    fi.useServerSideNormalization,
                    buildCache,
                    buildCacheOptions
                );
            })
            .do(async fi => {
                if (fi.generatorSource == undefined) {
                    return;
                }
                const buildCacheOptions =
                    fi.dependenciesHash != undefined
                        ? {
                              depsHash: fi.dependenciesHash,
                              generatorVersion: getGeneratorVersion(fi.generatorSource),
                              gfv: gfv,
                              mode: options.buildCacheMode,
                              farmDirectory: farmDirectory,
                          }
                        : undefined;
                await writeFLangNormalizationFiles(
                    fi.formName,
                    formDirPath,
                    resourcesDirectory,
                    undefined,
                    buildCache,
                    buildCacheOptions,
                    fi.buildCacheExist
                );
                await writeKClangFiles(
                    fi.formName,
                    formDirPath,
                    resourcesDirectory,
                    undefined,
                    buildCache,
                    buildCacheOptions,
                    fi.buildCacheExist
                );
                await writeExtendedSchemaFiles(
                    fi.formName,
                    formDirPath,
                    resourcesDirectory,
                    undefined,
                    buildCache,
                    buildCacheOptions,
                    fi.buildCacheExist
                );
                await writeAttachmentPathFiles(
                    fi.formName,
                    formDirPath,
                    resourcesDirectory,
                    undefined,
                    buildCache,
                    buildCacheOptions
                );
                await writeAttachmentPathByGfvFiles(
                    fi.formName,
                    formDirPath,
                    resourcesDirectory,
                    undefined,
                    buildCache,
                    buildCacheOptions
                );
            })
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "schemas"), rngSchemaMask).do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("SCHEMAS"),
                        x,
                        path.join(resourcesDirectory, "schemas")
                    )
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "converters"), "*").do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("CONVERTERS"),
                        x,
                        path.join(resourcesDirectory, "converters")
                    )
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "mergers"), "*").do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("MERGERS"),
                        x,
                        path.join(resourcesDirectory, "mergers")
                    )
                )
            )
            .attach(async fi => {
                const modifier = (content: string): string =>
                    content.replace(/<x:include href="[./]+/g, '<x:include href="');
                return Observe.files(path.join(fi.formSourcesPath, "converters"), "*").do(x =>
                    FSTask.modifyFilesContent(
                        getLogger().withPrefix("FIX-XSLT-INCLUDE"),
                        x,
                        path.join(resourcesDirectory, "converters"),
                        "**/*.xslt",
                        modifier
                    )
                );
            })
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "..", "..", "xsltTemplates"), "*").do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("XSLT-TEMPLATES"),
                        x,
                        path.join(resourcesDirectory, "converters", "xsltTemplates")
                    )
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "normalize"), "*.xml").do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("NORMALIZE"),
                        x,
                        path.join(resourcesDirectory, "normalize")
                    )
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "patch"), "*.xslt").do(x =>
                    FSTask.copyRecursively(getLogger().withPrefix("PATCH"), x, path.join(resourcesDirectory, "patch"))
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "map"), "*.xml").do(x =>
                    FSTask.copyRecursively(getLogger().withPrefix("MAP"), x, path.join(resourcesDirectory, "map"))
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "unmap"), "*.xml").do(x =>
                    FSTask.copyRecursively(getLogger().withPrefix("UNMAP"), x, path.join(resourcesDirectory, "unmap"))
                )
            )
            .do(async fi => {
                if (buildOptions.watch) {
                    notifier.notify({
                        title: `${fi.formName}: сборка завершена`,
                        message: `Engine: ${
                            fi.engineSource == undefined
                                ? "[NoEngine]"
                                : fi.engineSource.type === "LocalEngineSources"
                                  ? "local"
                                  : fi.engineSource.version
                        }`,
                    });
                }
            });
    };

    const buildFormsChain = Observe.fixedFormDirectories(
        formInfos.map(x => path.relative(repositoryRoot, x.formPath))
    ).splitToParallelChains(
        x => x,
        x => x,
        createChainForForm
    );

    const candyWidgetsConfigTask = await createClientViteConfigTask(engineDirectory);
    const buildCandyWidgetChain = !options.buildCandyWidget
        ? new EmptyProducer()
        : candyWidgetsConfigTask.chainWith(p => new BuildCandyWidgetTask(p));

    if (options.clean) {
        await cleanTargetDirectory(resourcesDirectory, context.logger);
    }

    if (buildOptions.watch) {
        const stopObserve1 = await pickListsChain.observe();
        const stopObserve2 = await buildFormsChain.observe();
        const stopObserve3 = await staticResourcesForProductionChain.observe();
        const stopObserve5 = await resourcesExternalConvertersChain.observe();
        await context.waitForExit();
        if (stopObserve3 != undefined) {
            await stopObserve3();
        }
        if (stopObserve5 != undefined) {
            await stopObserve5();
        }
        await stopObserve2();
        await stopObserve1();
    } else {
        const startDate = Date.now();
        try {
            await pickListsChain.currentValue;
            await resourcesExternalConvertersChain.currentValue;
            await staticResourcesForProductionChain.currentValue;
            await buildFormsChain.currentValue;
            await buildCandyWidgetChain.currentValue;
            // eslint-disable-next-line no-console
            console.log(chalk.greenBright("Build success"));
        } finally {
            const totalTime = Date.now() - startDate;
            // eslint-disable-next-line no-console
            console.log(`Total time: ${printTime(totalTime)}`);
        }
    }
}

async function cleanTargetDirectory(resourcesDirectory: string, logger: ILogger): Promise<void> {
    logger.log("Cleaning resource directory. " + resourcesDirectory);
    await emptyDir(resourcesDirectory);
    logger.log("Resource directory clean");
}
